// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the choice card. When the assistant cannot do something it says what and why, and
// offers the closest thing it can do: "Show your own logged time instead? [Yes, show my time]
// [No thanks]". Yes sends the action's prompt back to the assistant; the decline button only
// answers the card here. Either way the card then shows the answer and its buttons rest, for
// as long as this session keeps the app open.

import styles from '@app/features/konvo_assistant/components/AssistantChoice.module.css';
import {CheckIcon, InfoIcon} from '@app/features/ui/icons/Icons';
import type {AssistantChoiceBlock} from '@fluxer/schema/src/domains/konvo/AssistantResponse';
import {clsx} from 'clsx';
import {useState} from 'react';

/** Answers by card id, so a card that scrolls away and back (or shows in two places) stays answered. */
const answers = new Map<string, string>();
const MAX_ANSWERS = 200;

function remember(key: string, answer: string): void {
	answers.delete(key);
	answers.set(key, answer);
	while (answers.size > MAX_ANSWERS) answers.delete(answers.keys().next().value!);
}

export function resetChoiceAnswersForTesting(): void {
	answers.clear();
}

const DECLINED = '\u0000declined';

export function AssistantChoice({
	block,
	onAction,
	disabled = false,
	answerKey,
}: {
	block: AssistantChoiceBlock;
	onAction?: (prompt: string, action?: string) => void;
	disabled?: boolean;
	/** Stable id for this card (message id plus block index), so the answer survives a remount. */
	answerKey?: string;
}) {
	// Without a message id the answer lives as long as the card is on screen.
	const [answer, setAnswer] = useState<string | null>(() => (answerKey ? (answers.get(answerKey) ?? null) : null));
	const answered = answer != null;
	const choose = (value: string) => {
		if (answered) return;
		if (answerKey) remember(answerKey, value);
		setAnswer(value);
	};
	return (
		<section
			className={clsx(styles.choice, answered && styles.answered)}
			data-flx="konvo-assistant.choice"
			data-answered={answered ? 'true' : undefined}
		>
			<header className={styles.head}>
				<span className={styles.icon} aria-hidden="true">
					<InfoIcon size={16} />
				</span>
				<div className={styles.text}>
					<h3 className={styles.title}>{block.title}</h3>
					{block.reason && <p className={styles.reason}>{block.reason}</p>}
				</div>
			</header>
			{block.question && <p className={styles.question}>{block.question}</p>}
			{onAction && (
				<div className={styles.actions}>
					{block.actions.map((action, index) => {
						const chosen = answer === `action:${index}`;
						return (
							<button
								key={`${action.label}:${action.prompt}`}
								type="button"
								className={clsx(index === 0 ? styles.primary : styles.secondary, chosen && styles.chosen)}
								disabled={disabled || (answered && !chosen)}
								aria-pressed={answered ? chosen : undefined}
								aria-disabled={chosen || undefined}
								onClick={() => {
									if (answered) return;
									choose(`action:${index}`);
									onAction(action.prompt, 'choice');
								}}
								data-flx="konvo-assistant.choice.action"
							>
								{chosen && <CheckIcon size={13} weight="bold" aria-hidden="true" />}
								<span className={styles.label}>{action.label}</span>
							</button>
						);
					})}
					{block.decline_label && (
						<button
							type="button"
							className={clsx(styles.secondary, answer === DECLINED && styles.chosen)}
							disabled={disabled || (answered && answer !== DECLINED)}
							aria-pressed={answered ? answer === DECLINED : undefined}
							aria-disabled={answer === DECLINED || undefined}
							onClick={() => choose(DECLINED)}
							data-flx="konvo-assistant.choice.decline"
						>
							{answer === DECLINED && <CheckIcon size={13} weight="bold" aria-hidden="true" />}
							<span className={styles.label}>{block.decline_label}</span>
						</button>
					)}
				</div>
			)}
		</section>
	);
}
